import React from 'react';
import { BookmarkIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import { useLocation, useNavigate } from 'react-router-dom';
import { Contract } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useSavedOpportunities } from '../contexts/SavedOpportunitiesContext';
import { PENDING_SAVE_KEY } from '../constants/savedOpportunities';
import { trackEvent } from '../utils/analytics';

interface SaveOpportunityButtonProps {
  opportunity: Contract;
  surface: 'table' | 'card' | 'detail' | 'saved_page';
  compact?: boolean;
}

const SaveOpportunityButton: React.FC<SaveOpportunityButtonProps> = ({ opportunity, surface, compact = false }) => {
  const { isAuthenticated } = useAuth();
  const { isSaved, isSaving, toggleSaved } = useSavedOpportunities();
  const location = useLocation();
  const navigate = useNavigate();
  const saved = isSaved(opportunity);
  const pending = opportunity.id ? isSaving(opportunity.id) : false;
  const label = saved ? 'Saved' : 'Save';

  const onClick = async () => {
    if (!opportunity.id) return;
    if (!isAuthenticated) {
      const returnTo = `${location.pathname}${location.search}`;
      window.localStorage.setItem(PENDING_SAVE_KEY, JSON.stringify({ contractId: opportunity.id }));
      trackEvent('save_auth_prompt_viewed', { contract_id: opportunity.id, surface });
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
      return;
    }
    try {
      await toggleSaved(opportunity, surface);
    } catch {
      // The provider restores state and shows the recoverable error toast.
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!opportunity.id || pending}
      aria-label={`${label} opportunity: ${opportunity.title}`}
      aria-pressed={saved}
      title={saved ? 'Remove from Saved Opportunities' : 'Save opportunity'}
      className={`inline-flex min-h-10 items-center justify-center gap-1.5 rounded-full border text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-60 ${
        compact ? 'min-w-10 px-2.5' : 'px-3.5'
      } ${saved ? 'border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'}`}
    >
      {saved ? <BookmarkSolidIcon className="h-4 w-4" /> : <BookmarkIcon className="h-4 w-4" />}
      {!compact && <span>{label}</span>}
    </button>
  );
};

export default SaveOpportunityButton;
